import { withLogParticipants } from '../shared/log-participant-snapshot';
import { saveAppliedTruth, type TruthCommand } from '../shared/applied-truth-transport';
import { buildAppliedFrame, type AppliedFrameResult } from '../janus-engine/transport/applied-frame';
import { awaitEntityBaseReady } from '../shared/entity-compile-ready';
// Native trap adapter and committed-frame subscriber for arm, discern, disarm, open and trigger.
//
// The page decides everything it legitimately can from the rows it holds — the host's mounted payload and so its NATURE (the routed
// skill + tool), the Trap DC, the exact tool row it works with (carried first, then a floor row in reach — L5's usableStacks, never a
// buried row), and the flat skill check itself (janus-engine/traps/skill-check.ts on Lane A's skill_roll_arr twin; a check the page
// cannot model is refused before drawing). ONE command goes to trap_truth_commit_inc.php; the answer carries the
// outcome and exact saved-row/computed deltas. Sender and peers apply the same native frame.
// A handled verb NEVER reaches options/game/traps/{arm_trap,discern_trap}_inc.php (retired, R-### in docs/JANUS_CLIENT_TRUTH_STATUS.md).
import { DISCERN_TRAP, trapSkillFor } from '../janus-engine/traps/skill-check.js';
import { trapCommitFrame } from '../janus-engine/traps/trap-commit-frame.js';
import { possessorSceneContext } from './possessor-scene.js';
import { resolveDiscern, discernChargeRows } from '../janus-engine/traps/discern-save.js';
import { judgeDisarm, type DisarmSubmission, type DisarmVerdict } from '../janus-engine/traps/board-validators.js';
import { planDisarmBegin, trapClockMaintenance, MACHINING_TOOLS, ENCHANTING_TOOLS } from '../janus-engine/traps/disarm-begin.js';
import { resolveTrapArm } from '../janus-engine/traps/arm.js';
import { trapArmed } from '../janus-engine/traps/lifecycle.js';
import { withTrapClockMaintenance } from '../janus-engine/traps/clock-maintenance.js';
import { refuseUnported } from '../janus-engine/unported.js';
import { resolveTrapFire } from '../janus-engine/traps/fire.js';
import { planDisarmOutcome } from '../janus-engine/traps/perfect-ascension-20.js';
import { ecLean } from '../janus-engine/ability/context.js';
import { applyResourceValues, type ResourceReaders, type ResourceValues } from '../janus-engine/resources/resource-adjust.js';
import { asRow, findRow, templateIdOf, upper, type Row } from '../janus-engine/minigames/rows.js';
import { cachedInventoryRow } from '../janus-engine/inventory/cached-inventory-trees.js';
import { heldInventory } from '../shared/inventory-applied-truth';
import { browserRollSource, resumableRollSource } from '../janus-engine/actions/roll-source.js';
import { prepareDeathResources } from '../shared/spawn-browser.js';
import { actorEncounterFor, actorRowFor, bankConfirmedRev, confirmedRevFor, reconcileActorRow } from './movement-truth.js';
import { abilityTruthReaders, abilityTruthCampaign, hydrateTurnTriggerDefinitions } from './ability-truth.js';
import { planWithConsent } from './trigger-consent.js';
import { TRIGGER_CONSENT_REASON } from '../janus-engine/ability/trigger-consent.js';
import { fetchAndCacheObject } from './object-cache.js';
import { handleCasterUpdate } from './caster-update.js';
import { actorCanReachTarget } from '../shared/proximity-browser.js';
import type { CasterArr } from '../shared/array-types.js';

export const TRAP_TRUTH_SCHEMA = 'janus.trap.truth.v2';
const ENDPOINT = '/options/game/actions/trap_truth_commit_inc.php';
const RETRIES = 3;

const own = new Set<string>();
const remember = (id: string): void => { own.add(id); if (own.size > 256) own.delete(own.values().next().value!); };
const pageCaches = (): { objectCache?: unknown; casterCache?: unknown; encounterCache?: unknown } => (window as unknown as { FateTide?: { objectCache?: unknown; casterCache?: unknown; encounterCache?: unknown } }).FateTide ?? {};
const layerValue = (row: Row, key: string): unknown => { try { return ftLayerValue(row, key); } catch { return null; } };
const layerEntries = (row: Row, key: string): unknown[] => { try { const e = ftLayerEntries(row, key); return Array.isArray(e) ? e : []; } catch { return []; } };

function objectCacheGet(id: string): Row | null {
  const cache = pageCaches().objectCache as { get?: (k: string) => unknown } | undefined;
  return asRow(cache?.get?.(id)) ?? asRow(cache?.get?.(id.toLowerCase()));
}
function catalogDef(encounter: Row, actor: Row | null, host: Row, id: string): Row | null {
  const key = upper(id);
  for (const c of [asRow(host.catalog), asRow(encounter.catalog), asRow(actor?.catalog)]) { const d = asRow(c?.[key]); if (d && asRow(d.items)) return d; }
  const cached = objectCacheGet(key);
  return cached && asRow(cached.items) ? cached : null;
}
/** ft_trap_mounted_ability_ids(): the direct trap_ability layer + the applied template's trap_ability slot rows. */
import { mountedTrapAbilityIds as mountedIds, selectMountedTrapPayload } from '../janus-engine/traps/mounted.js';
export const mountedTrapAbilityIds = (host: Row): string[] => mountedIds(host, layerEntries);
/** The actor's compiled row WITH its carried tree (the encounter member when it carries `inventory`, else the caster cache). */
function actorTreeFor(encounterId: string, actorId: string): Row | null {
  let best = actorRowFor(encounterId, actorId) as unknown as Row | null;
  for (const cache of [pageCaches().casterCache, pageCaches().objectCache]) {
    if (!(cache instanceof Map)) continue;
    for (const value of cache.values()) {
      const row = asRow(value);
      if (row && upper(ecLean(row, 'encounter_creature_id')) === upper(actorId) && Array.isArray(row.inventory)
        && Number(ecLean(row, 'movement_rev') ?? 0) >= Number(ecLean(best ?? {}, 'movement_rev') ?? 0)) best = row;
    }
  }
  return best;
}
function itemTypeReader(encounter: Row, actor: Row | null): (row: Row) => string {
  return (row: Row): string => {
    const direct = asRow(row.items)?.item_type;
    if (typeof direct === 'string' && direct) return direct;
    const tid = templateIdOf(row);
    for (const catalog of [asRow(row.catalog), asRow(actor?.catalog), asRow(encounter.catalog)]) {
      const t = asRow(asRow(catalog?.[tid])?.items)?.item_type;
      if (typeof t === 'string' && t) return t;
    }
    const cached = asRow(objectCacheGet(tid)?.items)?.item_type;
    return typeof cached === 'string' ? cached : '';
  };
}
async function reachMap(actorId: string, encounter: Row, toolId: string): Promise<Map<string, boolean | null>> {
  const roots = new Set<string>();
  const walk = (rows: unknown, root: string | null, depth: number): void => {
    if (!Array.isArray(rows) || depth > 8) return;
    for (const raw of rows) {
      const r = asRow(raw); if (!r) continue;
      const id = upper(asRow(r.inventory)?.inventory_id ?? r.inventory_id);
      const top = root ?? id;
      if (templateIdOf(r) === toolId && top) roots.add(top);
      const kids = r.inventory_in_container ?? r.container_contents;
      if (Array.isArray(kids)) walk(kids, top, depth + 1);
    }
  };
  walk(encounter.encounter_inventories_arr, null, 0);
  const map = new Map<string, boolean | null>();
  await Promise.all([...roots].map(async id => { map.set(id, await actorCanReachTarget(actorId, id)); }));
  return map;
}
function prepareCommitFrame(command: Row): void {
  if (command.applied_frame) return;
  const eid = upper(command.encounter_id), aid = upper(command.actor_id), hid = upper(command.host_inventory_id);
  const encounter = actorEncounterFor(eid) as Row | null, actor = aid === eid ? null : actorTreeFor(eid, aid);
  if (!encounter || !actor && !(aid === eid && ['trap_arm', 'trap_trigger'].includes(String(command.action)))) throw Error('trap:frame-context-not-held');
  if (command.action === 'trap_disarm_cancel') {
    command.applied_frame = buildAppliedFrame({ operation_id: String(command.operation_id), encounter_id: eid, actor_id: aid,
      rev: Number(ecLean(actor!, 'movement_rev') ?? 0), member_revs: {}, rows: [], computed: {}, new_entities: [], deletes: [] });
    return;
  }
  let host = findRow([encounter.encounter_inventories_arr, actor?.inventory], hid)?.row;
  if (!host) for (const member of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
    host = findRow([asRow(member)?.inventory], hid)?.row;
    if (host) break;
  }
  if (!host) throw Error('trap:frame-host-not-held');
  const native = abilityTruthReaders(actor ?? host, encounter, new Set(actor ? [aid] : []));
  if (['trap_open', 'trap_trigger', 'trap_disarm_spring'].includes(String(command.action)) && asRow(command.fire)?.mode !== 'rolled-v2') {
    const maintenance = trapClockMaintenance(host, actor ?? host, native);
    if (maintenance.writes.length) command.maintenance = { rows: maintenance.rows, removals: maintenance.writes.map(op => ({ status_id: String(op.id), before: op.before })) };
    // This endpoint already stores maintenance beside the fire; nested interactions carry it inside no_fire.
    if (command.maintenance && asRow(command.fire)?.maintenance) {
      const { maintenance: _clock, ...fire } = command.fire as Row;
      command.fire = fire;
    }
  }
  command.applied_frame = trapCommitFrame(command, encounter, actor, host, abilityTruthCampaign(encounter),
    { definition: id => catalogDef(encounter, actor, host!, id) ?? native.definition(id) },
    (member, roster) => possessorSceneContext(encounter, member, roster));
}
async function saveDueClocks(encounterId: string, actorId: string, hostId: string): Promise<void> {
  const encounter = actorEncounterFor(encounterId) as Row | null, actor = actorTreeFor(encounterId, actorId);
  const host = encounter && findRow([encounter.encounter_inventories_arr, actor?.inventory], hostId)?.row;
  if (!encounter || !actor || !host) return;
  const read = abilityTruthReaders(actor, encounter, new Set());
  if (!trapClockMaintenance(host, actor, read).writes.length) return;
  const result = await post({ schema: TRAP_TRUTH_SCHEMA, action: 'trap_open', operation_id: crypto.randomUUID().toUpperCase(),
    encounter_id: encounterId, actor_id: actorId, host_inventory_id: hostId, expected_rev: confirmedRevFor(encounterId, actorId, actor as never), fire: { mode: 'no_fire' } });
  if (result?.body?.ok !== true) throw Error(String(result?.body?.error ?? 'trap-clock:save-unconfirmed'));
}
async function post(command: Row, keepalive = false): Promise<{ status: number; body: Row | null } | null> {
  prepareCommitFrame(command);
  const { applied_frame, ...wire } = command;
  window.ftPushAppliedTruth?.({ type: 'janus_trap_truth', command: wire as TruthCommand,
    applied_frame: applied_frame as AppliedFrameResult });
  for (let attempt = 1; attempt <= RETRIES; attempt++) {
    try {
      const response = await saveAppliedTruth(ENDPOINT, command as TruthCommand, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(withLogParticipants(command)), ...(keepalive ? { keepalive: true } : { signal: AbortSignal.timeout(20000) }) });
      let body: Row | null = null;
      try { body = await response.json() as Row; } catch { body = null; }
      if (response.status >= 500 && (response.status !== 503 || body?.error === 'fire_lock_retry') && attempt < RETRIES) { await new Promise(r => setTimeout(r, 400 * attempt)); continue; }
      if (body?.ok === true && !(body.gm === true && body.applied === false)) {
        body.applied_frame ??= command.applied_frame;
        if (!window.ftPushAppliedTruth) await refreshDiscern(body, String(command.encounter_id));
      }
      return { status: response.status, body };
    } catch {
      if (attempt < RETRIES) { await new Promise(r => setTimeout(r, 400 * attempt)); continue; }
    }
  }
  return null;
}
const resourceReaders: ResourceReaders = {
  layer: (row, key) => layerValue(row, key),
  hasLayer: (row, key) => { const v = layerValue(row, key); return v !== null && v !== undefined; },
};
/** A fired trap's VICTIM: its saved columns (the answer, or a committed frame for a player character) onto this page's rows — the roster
 * member AND the caster cache (the two can be distinct objects on an observer's page) — vitality re-derived the resource lane's way, the
 * revision banked through the shared ledger (never older than the confirmed one). */
function applyVictim(encounterId: string, saved: unknown): void {
  const v = asRow(saved), values = asRow(v?.values);
  const victimId = upper(v?.id);
  if (!values || !victimId) return;
  const rev = Number(values.movement_rev);
  const rows = new Set<Row>();
  const member = actorRowFor(encounterId, victimId) as unknown as Row | null;
  if (member) rows.add(member);
  const cached = asRow((pageCaches().casterCache as { get?: (k: string) => unknown } | undefined)?.get?.(victimId));
  if (cached) rows.add(cached);
  for (const row of rows) {
    const ec = (asRow(row.encounter_creatures) ?? (row.encounter_creatures = {})) as Row;
    if (Number.isInteger(rev) && Number.isInteger(Number(ec.movement_rev)) && Number(ec.movement_rev) > rev) continue;
    const vit: ResourceValues = {};
    for (const col of ['v_current', 'v_temp'] as const) if (Number.isInteger(values[col])) vit[col] = Number(values[col]);
    applyResourceValues(row, vit, resourceReaders, null);
    for (const col of ['luck', 'reaction', 'encounter_creature_dead']) if (Number.isInteger(values[col])) ec[col] = values[col];
    if (row === member && Number.isInteger(rev)) {
      bankConfirmedRev(encounterId, victimId, row as never, rev, {});
      reconcileActorRow(encounterId, victimId, row as never);
    } else if (Number.isInteger(rev)) ec.movement_rev = rev; // the caster-cache copy carries the revision it now reflects
    try { handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true }); } catch { /* repaint is best effort */ }
  }
}
/** The committed actor columns (AP / luck) onto this page's member row, then a repaint. */
function applyActor(encounterId: string, actorId: string, body: Row): void {
  if (upper(actorId) === upper(encounterId)) return;
  const row = actorRowFor(encounterId, actorId) as unknown as Row | null;
  if (!row) return;
  const ec = (asRow(row.encounter_creatures) ?? (row.encounter_creatures = {})) as Row;
  let changed = false;
  if (Number.isInteger(body.movement_rev)) {
    // SAVED truth is banked through the shared actor-revision ledger (closing review #6): an answer / frame older than the CONFIRMED
    // revision is ignored (never the rendered one — an optimistic move may be rendered ahead of saved truth), a newer one is banked
    // with its AP, and the row is reconciled pending-aware (queued optimistic moves re-apply on top), so a later reconcile can never
    // restore the pre-trap AP.
    const rev = Number(body.movement_rev);
    if (rev < confirmedRevFor(encounterId, actorId, row as never)) return;
    bankConfirmedRev(encounterId, actorId, row as never, rev, Number.isInteger(body.aps) ? { aps: Number(body.aps) } : {});
    changed = reconcileActorRow(encounterId, actorId, row as never) || changed;
  }
  for (const column of ['luck', 'reaction']) if (Number.isInteger(body[column]) && ec[column] !== body[column]) { ec[column] = body[column]; changed = true; }
  if (changed) { try { handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true }); } catch { /* repaint is best effort */ } }
}

/** Sender and observers install the same native rows and changed computed clusters. */
async function refreshDiscern(body: Row, encounterId: string): Promise<void> {
  const applied = body.applied_frame;
  if (!applied) return;
  await window.ftApplyBasePatch?.({ type: 'base_patch', encounter_id: encounterId, ops: [],
    applied: typeof applied === 'string' ? applied : JSON.stringify(applied) });
}

export const TRAP_MESSAGES: Record<string, string> = {
  check_unmodelled: 'This trap check uses a rule the browser cannot resolve yet — it has been reported.',
  not_armed: 'Nothing armed here.',
  already_armed: 'It is already armed.',
  no_trap_mounted: 'No trap payload mounted — place it from the encounter Traps tab, or apply a crafted trap payload to this object.',
  missing_tools: 'You lack the tools this work demands.',
  tools_out_of_reach: 'Your tools are not within reach.',
  not_enough_aps: 'Not enough action points.',
  host_gone: 'That object is not here.',
  session_expired: 'The attempt timed out. Open it again to retry.',
  verdict_mismatch: 'The mechanism did not respond the way you expected. Open it again to retry.',
  host_busy: 'Someone else is working on that trap. Try again.',
};
export type TrapVerbOutcome =
  | { outcome: 'handled'; success: boolean; total: number | null; dc: number | null; mode: string; operationId: string }
  | { outcome: 'refused'; error: string; message: string; operationId?: string };

/** Arm admission, all mounted definitions and area/lifecycle context are held before any player draw. */
async function commitArm(input: { encounterId: string; actorId: string; hostInventoryId: string; rollPrivate?: number; gm?: boolean }): Promise<TrapVerbOutcome> {
  const encounterId = upper(input.encounterId), actorId = upper(input.actorId), hostId = upper(input.hostInventoryId);
  const refuse = (error: string): TrapVerbOutcome => {
    refuseUnported(error);
    return { outcome: 'refused', error, message: TRAP_MESSAGES[error] ?? error };
  };
  try {
    const principal = actorId === encounterId && input.gm === true;
    const encounter = actorEncounterFor(encounterId) as Row | null, actor = principal ? null : actorTreeFor(encounterId, actorId);
    if (!encounter || !actor && !principal) return refuse('trap-arm:actor-or-encounter-not-held');
    const host = findRow([encounter.encounter_inventories_arr, actor?.inventory,
      ...(Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr.map(raw => asRow(raw)?.inventory) : [])], hostId)?.row;
    if (!host) return refuse('host_gone');
    const ids = ['78C51D2D-5178-551C-1249-B5DDA4083098', ...(input.gm ? [] : [trapSkillFor('mechanical'), trapSkillFor('magical')]), ...mountedTrapAbilityIds(host)];
    await Promise.all(ids.filter(id => !catalogDef(encounter, actor, host, id)).map(id => fetchAndCacheObject(id)));
    const defs = ids.map(id => catalogDef(encounter, actor, host, id)).filter((def): def is Row => !!def);
    await hydrateTurnTriggerDefinitions(actor ?? host, encounter, defs, [...(actor ? [actor] : []), host, ...(Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [])]);
    const native = abilityTruthReaders(actor ?? host, encounter, new Set(actor ? [actorId] : []));
    const reach = input.gm ? new Map<string, boolean | null>() : await reachMap(actorId, encounter, ENCHANTING_TOOLS);
    if (!input.gm) for (const [id, reachable] of await reachMap(actorId, encounter, MACHINING_TOOLS)) reach.set(id, reachable);
    const read = { ...native, definition: (id: string) => catalogDef(encounter, actor, host, id) ?? native.definition(id),
      itemType: itemTypeReader(encounter, actor), reachable: (id: string) => reach.get(upper(id)) ?? null };
    const members = new Map<string, Row>();
    for (const raw of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
      const row = asRow(raw), id = upper(asRow(row?.encounter_creatures)?.encounter_creature_id);
      if (row && id) members.set(id, row);
    }
    if (actor) members.set(actorId, actor);
    const rollPrivate = [0, 1, 2].includes(Number(input.rollPrivate)) ? Number(input.rollPrivate) : 0;
    const rolls = resumableRollSource(browserRollSource());
    const decision = await planWithConsent(encounterId, actorId, consent => resolveTrapArm({ encounter, members, actor, host,
      read: { ...read, triggerConsent: consent }, gm: input.gm === true, rollPrivate }, rolls.next()),
    result => !result.ok && result.error.includes(TRIGGER_CONSENT_REASON));
    if (!decision.ok) return refuse(decision.error);
    const command: Row = { schema: TRAP_TRUTH_SCHEMA, action: 'trap_arm', operation_id: crypto.randomUUID().toUpperCase(),
      encounter_id: encounterId, actor_id: actorId, host_inventory_id: hostId,
      expected_rev: actor ? confirmedRevFor(encounterId, actorId, actor as never) : 0, roll_private: rollPrivate, arm: decision.plan };
    remember(String(command.operation_id));
    const answer = await post(command), body = answer?.body;
    if (answer && answer.status >= 200 && answer.status < 300 && body?.ok === true) {
      applyActor(encounterId, actorId, body);
      window.dispatchEvent(new CustomEvent('ft:trap-truth-committed', { detail: { command, result: body } }));
      return { outcome: 'handled', success: body.success === true, total: decision.plan.total, dc: decision.plan.dc,
        mode: decision.plan.mode, operationId: String(command.operation_id) };
    }
    return refuse(String(body?.error ?? (answer ? `http_${answer.status}` : 'network')));
  } catch (error) { return refuse('trap-arm:context-not-held:' + String((error as Error)?.message ?? error).slice(0, 100)); }
}

/**
 * Arm / Discern from the object card (or a scene radial). Null = this page cannot resolve the actor or the host (no encounter row on
 * the page) — the caller shows its own failure; the retired legacy endpoint is never called. `gm` = the viewer is the full GM (the arm
 * bypass: no roll, no tools — the server proves it from the encounter's own campaign).
 */
export async function tryCommitTrapVerb(input: { verb: 'arm' | 'discern'; encounterId: string; actorId: string; hostInventoryId: string; rollPrivate?: number; gm?: boolean }): Promise<TrapVerbOutcome | null> {
  if (!await awaitEntityBaseReady([...new Set([input.encounterId, input.actorId])])) {
    if (input.verb !== 'arm') return null;
    const error = 'trap-arm:compile-not-ready';
    refuseUnported(error);
    return { outcome: 'refused', error, message: error };
  }
  if (input.verb === 'arm') return commitArm(input);
  const encounterId = upper(input.encounterId), actorId = upper(input.actorId), hostId = upper(input.hostInventoryId);
  let encounter = actorEncounterFor(encounterId) as Row | null;
  let actor = actorTreeFor(encounterId, actorId);
  if (!encounter || !actor) return null;
  const refuse = (error: string): TrapVerbOutcome => ({ outcome: 'refused', error, message: TRAP_MESSAGES[error] ?? error });
  // Hydrate the actor, mounted definition and trigger closure before taking the snapshot or drawing.
  let command: Row;
  try {
    encounter = actorEncounterFor(encounterId) as Row | null;
    actor = actorTreeFor(encounterId, actorId);
    if (!encounter || !actor) return null;
    const hit = findRow([encounter.encounter_inventories_arr, actor.inventory], hostId);
    if (!hit) return refuse('host_gone');
    const host = hit.row;
    const mounted = mountedTrapAbilityIds(host);
    for (const id of mounted)
      if (!catalogDef(encounter, actor, host, id)) await fetchAndCacheObject(id);
    const payload = mounted[0] ? catalogDef(encounter, actor, host, mounted[0]) : null;
    const skill = trapSkillFor(payload && String(layerValue(payload, 'trap_classification') ?? '').trim().toLowerCase() === 'magical' ? 'magical' : 'mechanical');
    for (const id of [skill, DISCERN_TRAP]) if (!catalogDef(encounter, actor, host, id)) await fetchAndCacheObject(id);
    await hydrateTurnTriggerDefinitions(actor, encounter, [], [actor]);
    // Re-read after hydration so both AP and the revision belong to this actual check snapshot.
    encounter = actorEncounterFor(encounterId) as Row | null;
    actor = actorRowFor(encounterId, actorId) as unknown as Row | null;
    if (!encounter || !actor) return null;
    const freshHost = findRow([encounter.encounter_inventories_arr, actor.inventory], hostId)?.row;
    if (!freshHost) return refuse('host_gone');
    const members = new Map<string, Row>();
    for (const raw of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
      const row = asRow(raw), id = upper(asRow(row?.encounter_creatures)?.encounter_creature_id);
      if (row && id) members.set(id, row);
    }
    members.set(actorId, actor);
    const native = abilityTruthReaders(actor, encounter, new Set([actorId]));
    const read = { ...native, definition: (id: string) => catalogDef(encounter!, actor, freshHost, id) ?? native.definition(id) };
    const rollPrivate = [0, 1, 2].includes(Number(input.rollPrivate)) ? Number(input.rollPrivate) : 0;
    const decision = resolveDiscern({ encounter, members, actorId, host: freshHost, read,
      rollPrivate, chargeRows: discernChargeRows(actor, encounter) }, browserRollSource());
    if (!decision.ok) { await saveDueClocks(encounterId, actorId, hostId); return refuse(decision.error); }
    command = { schema: TRAP_TRUTH_SCHEMA, action: 'trap_discern', operation_id: crypto.randomUUID().toUpperCase(),
      encounter_id: encounterId, actor_id: actorId, expected_rev: confirmedRevFor(encounterId, actorId, actor as never),
      host_inventory_id: hostId, check: { mode: 'rolled', ...decision.plan }, roll_private: rollPrivate };
  } catch (error) { return refuse('trap_check:' + String((error as Error)?.message ?? error).slice(0, 100)); }
  remember(String(command.operation_id));
  window.dispatchEvent(new CustomEvent('ft:trap-truth-applied', { detail: { command } }));
  const answer = await post(command);
  const body = answer?.body ?? null;
  if (answer && answer.status >= 200 && answer.status < 300 && body?.ok === true) {
    applyActor(encounterId, actorId, body);
    window.dispatchEvent(new CustomEvent('ft:trap-truth-committed', { detail: { command, result: body } }));
    return { outcome: 'handled', success: body.success === true, total: Number.isInteger(body.total) ? Number(body.total) : null,
      dc: Number.isInteger(body.dc) ? Number(body.dc) : null, mode: String(body.mode ?? ''), operationId: String(command.operation_id) };
  }
  const error = String(body?.error ?? (answer ? `http_${answer.status}` : 'network'));
  window.dispatchEvent(new CustomEvent('ft:trap-truth-refused', { detail: { command, error, status: answer?.status ?? 0 } }));
  return { outcome: 'refused', error, message: TRAP_MESSAGES[error] ?? 'Discern failed: ' + error, operationId: String(command.operation_id) };
}

// =================================================================================================================================
// THE DISARM MINIGAME (S2/S3) — the session the shell (trap-disarm/trap-disarm.ts) holds instead of the retired server token.
// =================================================================================================================================
export interface TrapDisarmSession { encounterId: string; actorId: string; hostId: string; attemptId: string; geometry: unknown; rollPrivate: number; skill: 'machining' | 'magic' }
export type TrapDisarmBeginOutcome =
  | { outcome: 'handled'; session: TrapDisarmSession; config: Row }
  | { outcome: 'refused'; error: string; message: string };
export type TrapDisarmStepOutcome = { ok: true; body: Row } | { ok: false; error: string; status: number; body: Row | null };

/** The browser plans the entire attempt before posting; the commit stores its board and CASes its AP. */
export async function trapDisarmBegin(input: { encounterId: string; actorId: string; hostInventoryId: string; rollPrivate?: number }): Promise<TrapDisarmBeginOutcome | null> {
  const encounterId = upper(input.encounterId), actorId = upper(input.actorId), hostId = upper(input.hostInventoryId);
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const actor = actorTreeFor(encounterId, actorId);
  if (!encounter || !actor) return null;
  const refuse = (error: string): TrapDisarmBeginOutcome => ({ outcome: 'refused', error, message: TRAP_MESSAGES[error] ?? error });
  const hit = findRow([encounter.encounter_inventories_arr, actor.inventory], hostId);
  if (!hit) return refuse('host_gone');
  const read = abilityTruthReaders(actor, encounter, new Set());
  const mounted = mountedTrapAbilityIds(hit.row);
  // Hydrate the same first payload as the legacy routing; an unarmed host needs no definition or tools.
  if (trapArmed(hit.row, read.statuses) && mounted[0] && !catalogDef(encounter, actor, hit.row, mounted[0])) {
    try { await fetchAndCacheObject(mounted[0]); } catch { /* missing page state, never a PHP fallback */ }
    if (!catalogDef(encounter, actor, hit.row, mounted[0])) return null;
  }
  const reach = new Map<string, boolean | null>();
  // The planner chooses routing. Reach facts for both tool families are read without a begin request.
  for (const template of [MACHINING_TOOLS, ENCHANTING_TOOLS]) for (const [id, value] of await reachMap(actorId, encounter, template)) reach.set(id, value);
  const member = actorRowFor(encounterId, actorId) as unknown as Row | null;
  const expectedRev = confirmedRevFor(encounterId, actorId, (member ?? actor) as never);
  const decision = planDisarmBegin({ host: hit.row, actor: { ...actor, encounter_creatures: member?.encounter_creatures ?? actor.encounter_creatures }, encounter,
    read: { ...read, definition: id => catalogDef(encounter, actor, hit.row, id), itemType: itemTypeReader(encounter, actor), reachable: id => reach.get(upper(id)) ?? null } });
  if (!decision.ok) {
    if (decision.maintenance.writes.length) {
      // A due boundary retires its clock and reports the existing R-102 skip; it never opens a disarm session.
      try { await saveDueClocks(encounterId, actorId, hostId); }
      catch (error) { return refuse(error instanceof Error ? error.message : 'network'); }
    }
    return refuse(decision.error);
  }
  const rollPrivate = [0, 1, 2].includes(Number(input.rollPrivate)) ? Number(input.rollPrivate) : 0;
  const command: Row = { schema: TRAP_TRUTH_SCHEMA, action: 'trap_disarm_begin', operation_id: crypto.randomUUID().toUpperCase(), encounter_id: encounterId, actor_id: actorId,
    expected_rev: expectedRev, host_inventory_id: hostId,
    tool_inventory_id: decision.plan.tool_inventory_id, roll_private: rollPrivate, plan: decision.plan };
  remember(String(command.operation_id));
  const answer = await post(command);
  const body = answer?.body ?? null;
  if (!answer || answer.status < 200 || answer.status >= 300 || body?.ok !== true) return refuse(String(body?.error ?? (answer ? `http_${answer.status}` : 'network')));
  applyActor(encounterId, actorId, body);
  const session: TrapDisarmSession = { encounterId, actorId, hostId, attemptId: String(body.attempt_id ?? ''), geometry: body.geometry, rollPrivate, skill: decision.plan.skill_var };
  window.dispatchEvent(new CustomEvent('ft:trap-truth-committed', { detail: { command, result: body } }));
  return { outcome: 'handled', session, config: body };
}
async function disarmStep(session: TrapDisarmSession, action: string, extra: Row, keepalive = false): Promise<TrapDisarmStepOutcome> {
  const command: Row = { schema: TRAP_TRUTH_SCHEMA, action, operation_id: crypto.randomUUID().toUpperCase(), encounter_id: session.encounterId, actor_id: session.actorId,
    expected_rev: 0, host_inventory_id: session.hostId, attempt_id: session.attemptId, roll_private: session.rollPrivate, ...extra };
  remember(String(command.operation_id));
  const answer = await post(command, keepalive);
  const body = answer?.body ?? null;
  window.dispatchEvent(new CustomEvent('ft:trap-truth-step', { detail: { command, status: answer?.status ?? 0, body } }));
  if (answer && answer.status >= 200 && answer.status < 300 && body?.ok === true) {
    if (body.victim && !body.applied_frame) applyVictim(session.encounterId, body.victim);
    return { ok: true, body };
  }
  return { ok: false, error: String(body?.error ?? (answer ? `http_${answer.status}` : 'network')), status: answer?.status ?? 0, body };
}
/** The board, cleanup and earned progression are decided together before the single typed save. */
export async function trapDisarmResult(session: TrapDisarmSession, submission: DisarmSubmission): Promise<TrapDisarmStepOutcome & { verdict: DisarmVerdict }> {
  const verdict = judgeDisarm(session.geometry, submission);
  let plan: ReturnType<typeof planDisarmOutcome>;
  try {
    const encounter = actorEncounterFor(session.encounterId) as Row | null;
    const actor = actorTreeFor(session.encounterId, session.actorId);
    const host = encounter && findRow([encounter.encounter_inventories_arr, actor?.inventory], session.hostId)?.row;
    if (!encounter || !actor || !host) throw Error('trap-disarm:rows-not-held');
    const native = abilityTruthReaders(actor, encounter, new Set([session.actorId]));
    const read = { ...native, definition: (id: string) => catalogDef(encounter, actor, host, id) ?? native.definition(id) };
    for (const id of mountedTrapAbilityIds(host)) if (!read.definition(id)) await fetchAndCacheObject(id);
    const members = new Map<string, Row>();
    for (const row of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
      const member = asRow(row), id = upper(asRow(member?.encounter_creatures)?.encounter_creature_id);
      if (member && id) members.set(id, member);
    }
    members.set(session.actorId, actor);
    plan = planDisarmOutcome({ encounter, actor, host, members, read, skill: session.skill, outcome: verdict }, browserRollSource());
  } catch (error) {
    const reason = String((error as { reason?: string; message?: string }).reason ?? (error as Error).message);
    refuseUnported(reason);
    return { ok: false, error: reason, status: 0, body: null, verdict };
  }
  // Keep the request acceptable to the previously served endpoint during bundle-first delivery.
  // The native saver ignores this compatibility field; an eligible plan already drew its one face.
  const confirmation = plan.save.rows.find(row => row.roll_type_1 === 'gained_ascension_skill_point' || row.roll_type_1 === 'failed_roll_for_ascension_skill_point');
  const ascensionFace = verdict.success && verdict.perfect ? confirmation?.roll_result ?? browserRollSource().d(20) : null;
  // A botched weave springs the trap AT THE DISARMER — its payload resolved here too.
  let out: TrapDisarmStepOutcome = { ok: false, error: 'unsent', status: 0, body: null };
  for (let attempt = 1; attempt <= 2; attempt++) {
    const fire = verdict.sprung ? await buildFire(session.encounterId, session.hostId, session.actorId, 'disarm_failure') : null;
    out = await disarmStep(session, 'trap_disarm_result', { submission, verdict: { success: verdict.success, sprung: verdict.sprung, perfect: verdict.perfect, ascension_face: ascensionFace, ...plan },
      ...(fire ? { fire } : {}) });
    if (out.ok || !fireRetryable({ status: out.status, body: out.body })) break;
  }
  return { ...out, verdict };
}
export async function trapDisarmSpring(session: TrapDisarmSession): Promise<TrapDisarmStepOutcome> {
  let out: TrapDisarmStepOutcome = { ok: false, error: 'unsent', status: 0, body: null };
  for (let attempt = 1; attempt <= 2; attempt++) {
    const fire = await buildFire(session.encounterId, session.hostId, session.actorId, 'disarm_failure');
    out = await disarmStep(session, 'trap_disarm_spring', fire ? { fire } : {});
    if (out.ok || !fireRetryable({ status: out.status, body: out.body })) break;
  }
  return out;
}
export const trapDisarmCancel = (session: TrapDisarmSession, keepalive = false): Promise<TrapDisarmStepOutcome> => disarmStep(session, 'trap_disarm_cancel', {}, keepalive);

// =================================================================================================================================
// A FIRED TRAP (owner 2026-09-28: the payload is resolved HERE — janus-engine/traps/fire.ts — and the commit records it; the server never
// rolls a trap). Open, GM trigger and disarm share the page's armed gate; no_fire explicitly records a no-op interaction.
// =================================================================================================================================
/** A fire the commit refused because the page's victim row was behind the database (or the page sent none): worth one fresh retry. */
const fireRetryable = (answer: { status: number; body: Row | null } | null): boolean =>
  answer?.status === 409 && /^fire_(stale|verdict_required|victim_gone)/.test(String(answer.body?.error ?? ''));
export type TrapInteraction = 'open' | 'touch' | 'pick_up' | 'unlock_attempt' | 'remote' | 'disarm_failure' | 'step_on' | 'magical_creature_detection';
/** A walk-step claim names the exact mounted sensor whose payload fires. */
async function buildFire(encounterId: string, hostId: string, victimId: string, interaction: TrapInteraction, onlyPayloadId?: string): Promise<Row | null> {
  if (!await awaitEntityBaseReady([encounterId, victimId])) return { mode: 'unmodelled', reason: 'trap:entity-base-unavailable', victim_id: victimId };
  let encounter = actorEncounterFor(encounterId) as Row | null;
  const victimTree = actorTreeFor(encounterId, victimId);
  if (!encounter) return { mode: 'unmodelled', reason: 'trap-fire:encounter-not-held', victim_id: victimId };
  // Match the move planner's held roots: loot may still belong to another creature, including a cache-only carrier.
  const heldHost = (): Row | null => {
    const caches = pageCaches();
    for (const cache of [caches.casterCache, caches.encounterCache, caches.objectCache]) {
      if (!(cache instanceof Map)) continue;
      for (const value of cache.values()) {
        const row = cachedInventoryRow(value, hostId);
        if (row) return row;
      }
    }
    return cachedInventoryRow(encounter, hostId) ?? cachedInventoryRow(victimTree, hostId);
  };
  const host = heldHost();
  if (!host) return { mode: 'unmodelled', reason: 'trap-fire:host-not-held', victim_id: victimId };
  if (!victimTree) return { mode: 'unmodelled', reason: 'trap-fire:actor-not-held', victim_id: victimId };
  const clock = trapClockMaintenance(host, victimTree, abilityTruthReaders(victimTree, encounter, new Set()));
  const noFire = (): Row => withTrapClockMaintenance({ mode: 'no_fire' }, clock);
  if (!trapArmed(host, row => ftCreatureStatuses(row, 'target') as Row[])) return noFire();
  for (const id of mountedTrapAbilityIds(host)) {
    if (!onlyPayloadId || upper(id) === upper(onlyPayloadId))
      if (!catalogDef(encounter, victimTree, host, id)) { try { await fetchAndCacheObject(id); } catch { /* selector reports the missing definition */ } }
  }
  const selected = selectMountedTrapPayload(host, interaction, { entries: (row, key) => row ? layerEntries(row, key) : [], definition: id => catalogDef(encounter!, victimTree, host, id) }, onlyPayloadId);
  if (selected.kind === 'unmodelled') return { mode: 'unmodelled', reason: selected.reason, ability_id: selected.id, victim_id: victimId };
  if (selected.kind === 'no_fire') return noFire();
  const payloadId = selected.id;
  try {
    if (!await awaitEntityBaseReady([encounterId, victimId])) return { mode: 'unmodelled', reason: 'trap:entity-base-unavailable', ability_id: payloadId, victim_id: victimId };
    // Every definition the payload reads, held BEFORE resolving (an unheld one would read as unmodelled): its damage types, contestee
    // skills and failed-save statuses, Fortunate (a natural 20 in combat) and apply_player_dying's cascade (Prone / Dying / Crippled / Maimed).
    const def = catalogDef(encounter, victimTree, host, payloadId);
    const needed = new Set<string>(['AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8', 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA', '8AE129AF-52D8-CCE8-706A-85767DB52740',
      '99C862B9-D6AE-4E18-7803-A6F9B42A88E1', 'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D']);
    for (const key of ['damage_type', 'skill_roll_contestee', 'status_effect_applied_to_target_after_contesting_roll_failed'])
      for (const e of def ? layerEntries(def, key) : []) {
        const id = upper(typeof e === 'string' ? e.split(/[\s|]/)[0] : asRow(e)?.item_id ?? asRow(asRow(e)?.items)?.item_id);
        if (/^[0-9A-F-]{36}$/.test(id)) needed.add(id);
      }
    for (const id of needed) if (!catalogDef(encounter, victimTree, host, id)) { try { await fetchAndCacheObject(id); } catch { /* unheld → unmodelled */ } }
    encounter = actorEncounterFor(encounterId) as Row | null;
    // Resolve the held victim used by both the planner and serializer.
    const roster = actorRowFor(encounterId, victimId) as unknown as Row | null;
    const full = actorTreeFor(encounterId, victimId) ?? roster;
    if (!encounter || !full) return { mode: 'unmodelled', reason: 'victim-not-held', ability_id: payloadId, victim_id: victimId };
    const members = new Map<string, Row>();
    for (const entry of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
      const r = asRow(entry), id = upper(asRow(r?.encounter_creatures)?.encounter_creature_id);
      if (r && id) members.set(id, r);
    }
    members.set(victimId, full);
    const hostNow = heldHost() ?? host;
    // OWNER RULING 2026-10-02 (lane A12, AB1's cross-lane ask — AB1 closed, its file edited here): a PLAYER victim's optional reactions (Evade,
    // Violent Focus …) are a 5 s prompt to its controller, silence = decline; NPC / GM victims and non-campaign encounters auto-resolve (the
    // reader's controller rule). The page asks between the dry runs and the one real run; the answers are final.
    await hydrateTurnTriggerDefinitions(full, encounter, [], [...members.values(), hostNow]);
    const baseRead = abilityTruthReaders(full, encounter, new Set([victimId]));
    const scene = encounter, held = hostNow;
    const deathResources = await prepareDeathResources([...members.values()], scene, baseRead);
    const rolls = resumableRollSource(browserRollSource());
    const verdict = await planWithConsent(encounterId, victimId, consent => resolveTrapFire({ encounter: scene, members, host: held, hostId, victimId, abilityId: payloadId,
      read: { ...baseRead, deathResources, triggerConsent: consent } as never, rollPrivate: 0 }, rolls.next()),
      v => v.kind === 'unmodelled' && v.reason.includes(TRIGGER_CONSENT_REASON));
    if (verdict.kind === 'unmodelled') {
      console.warn('[TRAP-TRUTH] fire not modelled by the browser:', verdict.reason);
      return { mode: 'unmodelled', reason: verdict.reason, ability_id: payloadId, victim_id: victimId };
    }
    if (verdict.kind === 'no_fire') return noFire();
    return withTrapClockMaintenance({ mode: 'rolled-v2', ability_id: payloadId, victim_id: victimId, verdict: { rows: verdict.rows, ec: verdict.ec, ecBefore: verdict.ecBefore,
      statusesAdded: verdict.statusesAdded, statusesRemoved: verdict.statusesRemoved, statusStacks: verdict.statusStacks,
      ...(verdict.spawnRows ? { spawnRows: verdict.spawnRows } : {}),
      ...(verdict.teleportOffers?.length ? { teleportOffers: verdict.teleportOffers } : {}),
      charges: verdict.charges, pc: verdict.pc, deathTail: verdict.deathTail, lifecycle: verdict.lifecycle } }, clock);
  } catch (error) {
    return { mode: 'unmodelled', reason: `adapter:${String((error as Error)?.message ?? error).slice(0, 80)}`, ability_id: payloadId, victim_id: victimId };
  }
}

// =================================================================================================================================
// OPEN / TRIGGER (S4) — one command each (the card's open ping, the GM's manual "spring it now"), carrying the browser's fire verdict.
// =================================================================================================================================
async function simpleVerb(action: 'trap_open' | 'trap_trigger', input: { encounterId: string; actorId: string; hostInventoryId: string; targetCreatureId?: string; rollPrivate?: number })
  : Promise<{ ok: boolean; fired: number; error?: string; operationId: string }> {
  const principal = upper(input.actorId) === upper(input.encounterId) && action === 'trap_trigger';
  if (!await awaitEntityBaseReady([input.encounterId, ...(!principal ? [input.actorId] : []), ...(input.targetCreatureId ? [input.targetCreatureId] : [])]))
    return { ok: false, fired: 0, error: 'trap:entity-base-unavailable', operationId: '' };
  const encounterId = upper(input.encounterId), actorId = upper(input.actorId), hostId = upper(input.hostInventoryId);
  const member = principal ? null : actorRowFor(encounterId, actorId) as unknown as Row | null;
  // The victim: the opener, or the GM's aimed target (a GM opening a card is inspection — the server no-ops it before any fire).
  const victimId = action === 'trap_open' ? actorId : upper(input.targetCreatureId ?? '');
  let command: Row = {}, answer: { status: number; body: Row | null } | null = null, body: Row | null = null;
  for (let attempt = 1; attempt <= 2; attempt++) {
    const inspection = action === 'trap_open' && (window as Window & { ftActiveViewerIsFullGm?: () => boolean }).ftActiveViewerIsFullGm?.() === true;
    const fire = inspection ? { mode: 'no_fire' } : victimId ? await buildFire(encounterId, hostId, victimId, action === 'trap_open' ? 'open' : 'remote') : null;
    if (fire?.mode === 'unmodelled') {
      const error = String(fire.reason ?? 'trap-fire:unmodelled');
      refuseUnported(error);
      return { ok: false, fired: 0, error, operationId: '' };
    }
    command = { schema: TRAP_TRUTH_SCHEMA, action, operation_id: crypto.randomUUID().toUpperCase(), encounter_id: encounterId, actor_id: actorId,
      expected_rev: member ? confirmedRevFor(encounterId, actorId, member as never) : 0, host_inventory_id: hostId,
      ...(input.targetCreatureId ? { target_creature_id: upper(input.targetCreatureId) } : {}), roll_private: [0, 1, 2].includes(Number(input.rollPrivate)) ? Number(input.rollPrivate) : 0,
      ...(fire ? { fire } : {}) };
    remember(String(command.operation_id));
    answer = await post(command);
    body = answer?.body ?? null;
    if (!fireRetryable(answer)) break; // a verdict built on a row the server has since changed: re-read the victim, resolve again, once
  }
  if (answer && answer.status >= 200 && answer.status < 300 && body?.ok === true) {
    if (body.victim && !body.applied_frame) applyVictim(encounterId, body.victim);
    window.dispatchEvent(new CustomEvent('ft:trap-truth-committed', { detail: { command, result: body } }));
    return { ok: true, fired: Number(body.fired ?? 0), operationId: String(command.operation_id) };
  }
  const error = String(body?.error ?? (answer ? `http_${answer.status}` : 'network'));
  refuseUnported(TRAP_MESSAGES[error] ?? error, undefined, undefined, TRAP_MESSAGES[error] ? 'trap:' + error : undefined);
  return { ok: false, fired: 0, error, operationId: String(command.operation_id) };
}
/** The object card's open ping carries the page's armed/no-fire decision and reports refusals. */
export const tryCommitTrapOpen = (input: { encounterId: string; actorId: string; hostInventoryId: string }) => simpleVerb('trap_open', input);
/** GM "spring it now" (trigger_trap_inc.php's successor); an optional aimed target for a non-AOE payload. */
export const tryCommitTrapTrigger = (input: { encounterId: string; actorId: string; hostInventoryId: string; targetCreatureId?: string; rollPrivate?: number }) => simpleVerb('trap_trigger', input);

export type TrapTruthWireFrame = {
  schema: string; operation_id: string; encounter_id: string; actor_id: string; action: string; committed?: boolean;
  pushed?: boolean;
  applied_frame?: unknown; host_inventory_id?: string; success?: boolean; armed?: boolean; noticed?: boolean; aps?: number; luck?: number; movement_rev?: number; verb?: string;
};
/** Peers adopt the same saved base rows as the sender. */
/** For the other commits a trap can fire from (FT3D object manipulation's touch, the inventory pick-up, the lockpick unlock): the page's
 * fire verdict for (host, victim, interaction) — null when nothing on the host fires for it — and the saved-victim applier. */
Object.assign(window, { ftTrapBuildFire: async (encounterId: string, hostId: string, victimId: string, interaction: TrapInteraction) => {
  const fire = await buildFire(upper(encounterId), upper(hostId), upper(victimId), interaction);
  // Inventory pickup tries touch next when no pickup payload was selected.
  if (interaction === 'pick_up' && fire?.mode === 'no_fire')
    return fire.maintenance ? buildFire(upper(encounterId), upper(hostId), upper(victimId), 'touch') : null;
  return fire;
}, ftTrapApplyVictim: (encounterId: string, saved: unknown) => applyVictim(upper(encounterId), saved) });
window.ftInventoryMoveTrapFrame = payload => {
  const fire = asRow(payload.trap_fire);
  if (!fire || fire.mode === 'no_fire' && !fire.maintenance) return;
  const eid = upper(payload.encounter_id), aid = upper(payload.caster_id), hid = upper(payload.inventory_id);
  const encounter = actorEncounterFor(eid) as Row | null, actor = actorTreeFor(eid, aid), host = heldInventory(hid);
  if (!encounter || !actor || !host) throw Error('trap:frame-context-not-held');
  const native = abilityTruthReaders(actor, encounter, new Set([aid]));
  const tails = asRow(asRow(payload.client_decision)?.tails)?.verdict as import('../janus-engine/statuses/gm-boundary').GmBoundaryVerdict | undefined;
  const frame = trapCommitFrame({ action: 'trap_trigger', operation_id: payload.mutation_id, encounter_id: eid, actor_id: aid, host_inventory_id: hid, fire },
    encounter, actor, host, abilityTruthCampaign(encounter), { definition: id => catalogDef(encounter, actor, host, id) ?? native.definition(id) },
    (member, roster) => possessorSceneContext(encounter, member, roster), tails);
  return { ...frame, inputs: { ...frame.inputs, inventory_move_tails: !!tails } };
};
export function handleTrapTruth(command: TrapTruthWireFrame): void {
  if (command?.schema !== TRAP_TRUTH_SCHEMA) return;
  if (!command.pushed) void refreshDiscern(command as unknown as Row, command.encounter_id).catch(error => console.warn('[TRAP-TRUTH] saved-base refresh:', error));
  applyActor(command.encounter_id, command.actor_id, command as unknown as Row);
  const victim = (command as unknown as Row).victim;
  if (victim && !command.applied_frame) applyVictim(upper(command.encounter_id), victim);
  window.dispatchEvent(new CustomEvent('ft:trap-truth-frame', { detail: { command, own: own.has(command.operation_id) } }));
}

Object.assign(window, { ftTryCommitTrapVerb: tryCommitTrapVerb, ftTrapDisarmBegin: trapDisarmBegin, ftTrapDisarmResult: trapDisarmResult, ftTrapDisarmSpring: trapDisarmSpring, ftTrapDisarmCancel: trapDisarmCancel, ftTryCommitTrapOpen: tryCommitTrapOpen, ftTryCommitTrapTrigger: tryCommitTrapTrigger });
